import { Loader2, Search, FileSpreadsheet } from 'lucide-react';
import type { OrgMember } from './memberTypes';
import type { GoogleProductionPreview, GoogleProductionSample } from './exportTypes';
import { ExportDateInput } from './ExportDateInput';

// Keep the visible scope aligned with the relay export readers.
export const GOOGLE_SHEETS_VENUE_NOTE =
  'Google Sheets loads cover Kalshi, Polymarket, and Polymarket US. US buy economics use recorded fees or explicitly labeled preview fee estimates; unknown values remain blank.';
export const GOOGLE_SHEETS_ALL_VENUES_LABEL = 'All (Kalshi + Polymarket + Polymarket US)';

// The two Google Sheets tools on the Manager Exports tab (#343). Pure
// presentation: every value and every handler arrives as a prop from
// useExportWorkflows. Characterized by managerGoogleSheetsPanels.test.tsx.
//
// #486 — every action gates on `scopeReady` as its own first term. Three of the
// four were already unclickable while the scope was unresolved, but only as a
// side effect of #461 blanking the trader id. `Run` with `All org accounts`
// ticked needs no trader id, so it stayed live — and it is also the one path
// the relay does not refuse (`moving-test-load` skips its member check when
// allTraders is set; `resolveOrgId` trusts an admin's `?orgId=` unchecked).
// That relay behavior is READ FROM the route, never exercised — this ticket may
// not call the live Sheets endpoints.
//
// ⚠️ The three terms are NOT equally load-bearing. Removing `!scopeReady` from
// Run fails 3 tests; removing it from either production term, or both, fails
// none — and no better test would change that, since `googleProdTraderId`
// derives to '' whenever readiness is false. Both production terms are
// redundant TODAY and kept as a fence against a change to that derivation, not
// because anything exercises them. What IS proven is that they never over-gate:
// forcing `scopeReady` false fails 25 tests.

export function OrgGoogleSheetLoadPanel({
  spreadsheetId,
  targetSheetTitle,
  traderId,
  allTraders,
  fromDate,
  toDate,
  accessToken,
  sourceLabel,
  bettorLabel,
  bookLabel,
  accounts,
  scopeReady,
  busy,
  message,
  error,
  onSpreadsheetIdChange,
  onTargetSheetTitleChange,
  onTraderIdChange,
  onAllTradersChange,
  onFromDateChange,
  onToDateChange,
  onAccessTokenChange,
  onSourceLabelChange,
  onBettorLabelChange,
  onBookLabelChange,
  onRun,
  className = 'mb-6',
}: {
  spreadsheetId: string;
  targetSheetTitle: string;
  traderId: string;
  allTraders: boolean;
  fromDate: string;
  toDate: string;
  accessToken: string;
  sourceLabel: string;
  bettorLabel: string;
  bookLabel: string;
  accounts: OrgMember[];
  /** #486 — the effective organization is resolved and not mid-change. */
  scopeReady: boolean;
  busy: boolean;
  message: string | null;
  error: string | null;
  onSpreadsheetIdChange: (value: string) => void;
  onTargetSheetTitleChange: (value: string) => void;
  onTraderIdChange: (value: string) => void;
  onAllTradersChange: (value: boolean) => void;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onAccessTokenChange: (value: string) => void;
  onSourceLabelChange: (value: string) => void;
  onBettorLabelChange: (value: string) => void;
  onBookLabelChange: (value: string) => void;
  onRun: () => void;
  className?: string;
}) {
  return (
    <section className={`bg-card border-border rounded-lg border p-4 ${className}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileSpreadsheet size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold">Sheet Test Load</h3>
        </div>
        <button
          type="button"
          onClick={onRun}
          disabled={!scopeReady || busy || (!allTraders && !traderId) || !accessToken || !spreadsheetId || !targetSheetTitle}
          title="Create test tab"
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />}
          Run
        </button>
      </div>
      <p className="text-muted-foreground/70 -mt-2 mb-3 text-[11px]">This test load exports Kalshi fills only.</p>

      <div className="grid gap-2">
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
          <label>
            <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Account</span>
            <select
              value={traderId}
              onChange={(e) => onTraderIdChange(e.target.value)}
              disabled={busy || allTraders}
              className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
            >
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.username} ({account.role})
                </option>
              ))}
            </select>
          </label>
          <label className="border-border bg-muted/30 flex h-[30px] items-center gap-2 rounded-md border px-2 text-[11px] text-muted-foreground">
            <input
              type="checkbox"
              checked={allTraders}
              onChange={(e) => onAllTradersChange(e.target.checked)}
              disabled={busy}
              className="h-3.5 w-3.5"
            />
            <span>All org accounts</span>
          </label>
        </div>

        <label>
          <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Spreadsheet ID</span>
          <input
            value={spreadsheetId}
            onChange={(e) => onSpreadsheetIdChange(e.target.value)}
            disabled={busy}
            className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 font-mono text-[11px] focus:border-blue-500 focus:outline-none disabled:opacity-50"
          />
        </label>

        <label>
          <span className="text-muted-foreground mb-1 block text-[11px] font-medium">New test tab</span>
          <input
            value={targetSheetTitle}
            onChange={(e) => onTargetSheetTitleChange(e.target.value)}
            disabled={busy}
            className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
          />
        </label>

        <div className="grid gap-2 sm:grid-cols-2">
          <ExportDateInput
            id="sheet-load-from"
            label="From (blank for all history)"
            value={fromDate}
            onChange={onFromDateChange}
            disabled={busy}
          />
          <ExportDateInput
            id="sheet-load-to"
            label="To (blank for now)"
            value={toDate}
            onChange={onToDateChange}
            disabled={busy}
          />
        </div>

        <div className="grid gap-2 sm:grid-cols-3">
          <label>
            <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Originator</span>
            <input
              value={sourceLabel}
              onChange={(e) => onSourceLabelChange(e.target.value)}
              disabled={busy || allTraders}
              className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
            />
          </label>
          <label>
            <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Bettor</span>
            <input
              value={bettorLabel}
              onChange={(e) => onBettorLabelChange(e.target.value)}
              disabled={busy || allTraders}
              className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
            />
          </label>
          <label>
            <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Book</span>
            <input
              value={bookLabel}
              onChange={(e) => onBookLabelChange(e.target.value)}
              disabled={busy}
              className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
            />
          </label>
        </div>

        <label>
          <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Google access token</span>
          <input
            type="password"
            value={accessToken}
            onChange={(e) => onAccessTokenChange(e.target.value)}
            disabled={busy}
            autoComplete="off"
            className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 font-mono text-[11px] focus:border-blue-500 focus:outline-none disabled:opacity-50"
          />
        </label>
      </div>

      {message && (
        <div className="mt-3 rounded-md bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400">
          {message}
        </div>
      )}
      {error && (
        <div className="mt-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">
          {error}
        </div>
      )}
    </section>
  );
}

export function OrgGoogleSheetProductionUpsertPanel({
  spreadsheetId,
  sourceSheetTitle,
  targetSheetTitle,
  traderId,
  fromDate,
  toDate,
  venue,
  allowPolymarket,
  accessToken,
  sourceLabel,
  bettorLabel,
  bookLabel,
  accounts,
  scopeReady,
  preview,
  confirmation,
  busy,
  message,
  error,
  onSpreadsheetIdChange,
  onSourceSheetTitleChange,
  onTargetSheetTitleChange,
  onTraderIdChange,
  onFromDateChange,
  onToDateChange,
  onVenueChange,
  onAllowPolymarketChange,
  onAccessTokenChange,
  onSourceLabelChange,
  onBettorLabelChange,
  onBookLabelChange,
  onConfirmationChange,
  onPreview,
  onApply,
  className = 'mb-6',
}: {
  spreadsheetId: string;
  sourceSheetTitle: string;
  targetSheetTitle: string;
  traderId: string;
  fromDate: string;
  toDate: string;
  venue: 'kalshi' | 'polymarket' | 'polymarket_us' | 'all';
  allowPolymarket: boolean;
  accessToken: string;
  sourceLabel: string;
  bettorLabel: string;
  bookLabel: string;
  accounts: OrgMember[];
  /** #486 — the effective organization is resolved and not mid-change. */
  scopeReady: boolean;
  preview: GoogleProductionPreview | null;
  confirmation: string;
  busy: 'preview' | 'apply' | null;
  message: string | null;
  error: string | null;
  onSpreadsheetIdChange: (value: string) => void;
  onSourceSheetTitleChange: (value: string) => void;
  onTargetSheetTitleChange: (value: string) => void;
  onTraderIdChange: (value: string) => void;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onVenueChange: (value: 'kalshi' | 'polymarket' | 'polymarket_us' | 'all') => void;
  onAllowPolymarketChange: (value: boolean) => void;
  onAccessTokenChange: (value: string) => void;
  onSourceLabelChange: (value: string) => void;
  onBettorLabelChange: (value: string) => void;
  onBookLabelChange: (value: string) => void;
  onConfirmationChange: (value: string) => void;
  onPreview: () => void;
  onApply: () => void;
  className?: string;
}) {
  const polymarketSelected = venue !== 'kalshi';
  const previewDisabled = !scopeReady
    || Boolean(busy)
    || !spreadsheetId.trim()
    || !sourceSheetTitle.trim()
    || !targetSheetTitle.trim()
    || !traderId
    || !accessToken.trim()
    || (polymarketSelected && !allowPolymarket);
  const applyDisabled = !scopeReady
    || Boolean(busy)
    || !preview
    || !preview.canApply
    || !accessToken.trim()
    || confirmation.trim() !== preview.confirmationRequired;

  return (
    <section className={`bg-card border-border rounded-lg border p-4 ${className}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileSpreadsheet size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold">Production Sheet Upsert</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onPreview}
            disabled={previewDisabled}
            title="Preview sheet upsert"
            className="bg-secondary text-muted-foreground hover:text-foreground flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-50"
          >
            {busy === 'preview' ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
            Preview
          </button>
          <button
            type="button"
            onClick={onApply}
            disabled={applyDisabled}
            title="Apply sheet upsert"
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {busy === 'apply' ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />}
            Apply
          </button>
        </div>
      </div>
      <p className="text-muted-foreground/70 -mt-2 mb-3 text-[11px]">{GOOGLE_SHEETS_VENUE_NOTE}</p>

      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(20rem,0.9fr)]">
        <div className="grid gap-2">
          <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(8rem,0.5fr)]">
            <label>
              <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Spreadsheet ID</span>
              <input
                value={spreadsheetId}
                onChange={(e) => onSpreadsheetIdChange(e.target.value)}
                disabled={Boolean(busy)}
                className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 font-mono text-[11px] focus:border-blue-500 focus:outline-none disabled:opacity-50"
              />
            </label>
            <label>
              <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Target tab</span>
              <input
                value={targetSheetTitle}
                onChange={(e) => onTargetSheetTitleChange(e.target.value)}
                disabled={Boolean(busy)}
                className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
              />
            </label>
            <label>
              <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Source tab</span>
              <input
                value={sourceSheetTitle}
                onChange={(e) => onSourceSheetTitleChange(e.target.value)}
                disabled={Boolean(busy)}
                className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
              />
            </label>
          </div>

          <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_8rem_8rem_8rem]">
            <label>
              <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Account</span>
              <select
                value={traderId}
                onChange={(e) => onTraderIdChange(e.target.value)}
                disabled={Boolean(busy)}
                className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
              >
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.username} ({account.role})
                  </option>
                ))}
              </select>
            </label>
            <ExportDateInput
              id="sheet-prod-from"
              label="From"
              value={fromDate}
              onChange={onFromDateChange}
              disabled={Boolean(busy)}
            />
            <ExportDateInput
              id="sheet-prod-to"
              label="To"
              value={toDate}
              onChange={onToDateChange}
              disabled={Boolean(busy)}
            />
            <label>
              <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Venue</span>
              <select
                value={venue}
                onChange={(e) => onVenueChange(e.target.value as 'kalshi' | 'polymarket' | 'polymarket_us' | 'all')}
                disabled={Boolean(busy)}
                className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
              >
                <option value="kalshi">Kalshi</option>
                <option value="polymarket">Polymarket</option>
                <option value="polymarket_us">Polymarket US</option>
                <option value="all">{GOOGLE_SHEETS_ALL_VENUES_LABEL}</option>
              </select>
            </label>
          </div>

          {polymarketSelected && (
            <label className="border-border bg-muted/30 flex items-center gap-2 rounded-md border px-2 py-1.5 text-[11px] text-muted-foreground">
              <input
                type="checkbox"
                checked={allowPolymarket}
                onChange={(e) => onAllowPolymarketChange(e.target.checked)}
                disabled={Boolean(busy)}
                className="h-3.5 w-3.5"
              />
              <span>Allow non-Kalshi rows in this run</span>
            </label>
          )}

          <div className="grid gap-2 sm:grid-cols-3">
            <label>
              <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Originator</span>
              <input
                value={sourceLabel}
                onChange={(e) => onSourceLabelChange(e.target.value)}
                disabled={Boolean(busy)}
                className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
              />
            </label>
            <label>
              <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Bettor</span>
              <input
                value={bettorLabel}
                onChange={(e) => onBettorLabelChange(e.target.value)}
                disabled={Boolean(busy)}
                className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
              />
            </label>
            <label>
              <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Book</span>
              <input
                value={bookLabel}
                onChange={(e) => onBookLabelChange(e.target.value)}
                disabled={Boolean(busy)}
                className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
              />
            </label>
          </div>

          <label>
            <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Google access token</span>
            <input
              type="password"
              value={accessToken}
              onChange={(e) => onAccessTokenChange(e.target.value)}
              disabled={Boolean(busy)}
              autoComplete="off"
              className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 font-mono text-[11px] focus:border-blue-500 focus:outline-none disabled:opacity-50"
            />
          </label>
        </div>

        <div className="border-border min-h-48 rounded-md border p-3">
          {preview ? (
            <div className="space-y-3">
              <div className="space-y-1 text-[11px]">
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Workbook</span>
                  <span className="text-right font-medium">{preview.workbookTitle || preview.spreadsheetId}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Kind</span>
                  <span className={preview.workbookKind === 'production' ? 'text-amber-500' : 'text-emerald-500'}>
                    {preview.workbookKind}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Tab</span>
                  <span className="text-right">{preview.targetSheetTitle}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Fingerprint</span>
                  <span className="max-w-48 truncate font-mono" title={preview.fingerprint}>{preview.fingerprint}</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-[11px]">
                <ProductionCount label="G1 rows" value={preview.counts.g1RowCount} />
                <ProductionCount label="Insert" value={preview.counts.inserts} />
                <ProductionCount label="Update" value={preview.counts.updates} />
                <ProductionCount label="Seen" value={preview.counts.seen} />
                <ProductionCount label="Not seen" value={preview.counts.existingG1RowsNotSeen} />
                <ProductionCount label="Errors" value={preview.counts.errors} tone={preview.counts.errors > 0 ? 'red' : undefined} />
              </div>

              <div className="space-y-2">
                <ProductionSampleList title="Insert samples" samples={preview.samples.inserts} />
                <ProductionSampleList title="Update samples" samples={preview.samples.updates} />
                <ProductionSampleList title="Error samples" samples={preview.samples.errors} />
              </div>

              <label>
                <span className="text-muted-foreground mb-1 block text-[11px] font-medium">
                  Confirmation
                </span>
                <input
                  value={confirmation}
                  onChange={(e) => onConfirmationChange(e.target.value)}
                  disabled={Boolean(busy)}
                  placeholder={preview.confirmationRequired}
                  className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 font-mono text-[11px] focus:border-blue-500 focus:outline-none disabled:opacity-50"
                />
              </label>
            </div>
          ) : (
            <div className="flex h-full min-h-44 flex-col justify-center gap-2 text-center">
              <p className="text-muted-foreground text-xs">No preview yet.</p>
              <p className="text-muted-foreground/70 text-[11px]">Preview must pass before Apply is enabled.</p>
            </div>
          )}
        </div>
      </div>

      {message && (
        <div className="mt-3 rounded-md bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400">
          {message}
        </div>
      )}
      {error && (
        <div className="mt-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">
          {error}
        </div>
      )}
    </section>
  );
}

function ProductionCount({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'red';
}) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-border/60 py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-mono font-semibold ${tone === 'red' ? 'text-red-400' : 'text-foreground'}`}>
        {value.toLocaleString()}
      </span>
    </div>
  );
}

function ProductionSampleList({
  title,
  samples,
}: {
  title: string;
  samples: GoogleProductionSample[];
}) {
  if (samples.length === 0) return null;
  return (
    <div>
      <div className="text-muted-foreground mb-1 text-[10px] uppercase tracking-wider">{title}</div>
      <div className="max-h-28 overflow-auto text-[11px]">
        {samples.map((sample, index) => (
          <div key={`${sample.key ?? sample.reason ?? title}-${index}`} className="border-border/60 border-t py-1 first:border-t-0">
            <div className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate">{sample.betDescription || sample.key || sample.reason}</span>
              {sample.rowNumber && <span className="text-muted-foreground shrink-0 font-mono">r{sample.rowNumber}</span>}
            </div>
            <div className="text-muted-foreground flex min-w-0 gap-2">
              {sample.trader && <span className="shrink-0">{sample.trader}</span>}
              {sample.ticker && <span className="truncate">{sample.ticker}</span>}
            </div>
            {sample.reason && <div className="text-red-400">{sample.reason}</div>}
            {sample.changedColumns && sample.changedColumns.length > 0 && (
              <div className="text-muted-foreground truncate">
                {sample.changedColumns.join(', ')}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
